/**
 * DS Coder — technology stack detection for an attached GitHub repo.
 *
 * Purely deterministic: reads the repo's dependency manifests (already exposed
 * by /api/github/repo?route=dependencies), the file tree, and language stats.
 * No LLM call — detection must be fast, free, and reproducible.
 */

import type { GitHubDependencies } from '@/services/githubService';
import type { DetectedStack, DetectedTech, TechId } from './types';

type DepRule = { tech: TechId; label: string; packages: string[] };

/** npm package → technology. First match per tech wins; order sets priority. */
const NPM_RULES: DepRule[] = [
  { tech: 'nextjs', label: 'Next.js', packages: ['next'] },
  { tech: 'react', label: 'React', packages: ['react'] },
  { tech: 'vue', label: 'Vue 3', packages: ['vue', 'nuxt'] },
  { tech: 'svelte', label: 'Svelte', packages: ['svelte', '@sveltejs/kit'] },
  { tech: 'vite', label: 'Vite', packages: ['vite'] },
  { tech: 'typescript', label: 'TypeScript', packages: ['typescript'] },
  { tech: 'express', label: 'Express', packages: ['express'] },
  { tech: 'tailwind', label: 'Tailwind CSS', packages: ['tailwindcss'] },
  { tech: 'supabase', label: 'Supabase', packages: ['@supabase/supabase-js', '@supabase/ssr'] },
  { tech: 'prisma', label: 'Prisma', packages: ['prisma', '@prisma/client'] },
  { tech: 'drizzle', label: 'Drizzle ORM', packages: ['drizzle-orm'] },
  { tech: 'react-query', label: 'TanStack Query', packages: ['@tanstack/react-query'] },
  { tech: 'vercel', label: 'Vercel (serverless)', packages: ['@vercel/node', 'vercel'] },
];

/** Python package → technology (from requirements.txt package list). */
const PY_RULES: DepRule[] = [
  { tech: 'django', label: 'Django', packages: ['django'] },
  { tech: 'fastapi', label: 'FastAPI', packages: ['fastapi'] },
];

/** Marker files in the tree → technology. */
const FILE_RULES: Array<{ tech: TechId; label: string; markers: string[] }> = [
  { tech: 'typescript', label: 'TypeScript', markers: ['tsconfig.json'] },
  { tech: 'vite', label: 'Vite', markers: ['vite.config.ts', 'vite.config.js', 'vite.config.mjs'] },
  { tech: 'nextjs', label: 'Next.js', markers: ['next.config.js', 'next.config.mjs', 'next.config.ts'] },
  { tech: 'tailwind', label: 'Tailwind CSS', markers: ['tailwind.config.ts', 'tailwind.config.js', 'tailwind.config.cjs'] },
  { tech: 'vercel', label: 'Vercel (serverless)', markers: ['vercel.json'] },
  { tech: 'supabase', label: 'Supabase', markers: ['supabase/config.toml'] },
  { tech: 'go', label: 'Go', markers: ['go.mod'] },
  { tech: 'rust', label: 'Rust', markers: ['cargo.toml'] },
  { tech: 'python', label: 'Python', markers: ['requirements.txt', 'pyproject.toml', 'setup.py', 'pipfile'] },
  { tech: 'nodejs', label: 'Node.js', markers: ['package.json'] },
];

/** Language stats → technology (fallback when manifests are unavailable). */
const LANGUAGE_RULES: Array<{ tech: TechId; label: string; language: string }> = [
  { tech: 'typescript', label: 'TypeScript', language: 'typescript' },
  { tech: 'javascript', label: 'JavaScript', language: 'javascript' },
  { tech: 'python', label: 'Python', language: 'python' },
  { tech: 'go', label: 'Go', language: 'go' },
  { tech: 'rust', label: 'Rust', language: 'rust' },
  { tech: 'vue', label: 'Vue 3', language: 'vue' },
  { tech: 'svelte', label: 'Svelte', language: 'svelte' },
];

export interface StackDetectionInput {
  repoFullName: string;
  dependencies?: GitHubDependencies | null;
  /** Repo tree paths (relative), lowercased matching is handled internally. */
  treePaths?: string[];
  /** GitHub language byte stats, e.g. { TypeScript: 12345 }. */
  languages?: Record<string, number> | null;
}

export function detectTechStack(input: StackDetectionInput): DetectedStack {
  const techs = new Map<TechId, DetectedTech>();
  const dependencyNames: string[] = [];

  const addTech = (tech: DetectedTech) => {
    if (!techs.has(tech.id)) techs.set(tech.id, tech);
  };

  const node = input.dependencies?.nodejs;
  if (node) {
    const allDeps: Record<string, string> = {
      ...(node.dependencies ?? {}),
      ...(node.devDependencies ?? {}),
    };
    dependencyNames.push(...Object.keys(allDeps));
    addTech({ id: 'nodejs', label: 'Node.js', evidence: 'package.json' });
    for (const rule of NPM_RULES) {
      const hit = rule.packages.find((p) => p in allDeps);
      if (hit) {
        addTech({ id: rule.tech, label: rule.label, version: allDeps[hit], evidence: `package.json: ${hit}` });
      }
    }
  }

  const pyPackages = input.dependencies?.python?.packages ?? [];
  if (pyPackages.length > 0 || input.dependencies?.pythonProject) {
    addTech({ id: 'python', label: 'Python', evidence: 'requirements.txt / pyproject.toml' });
    const normalized = pyPackages.map((p) => p.toLowerCase().split(/[=<>~![\s]/)[0]);
    dependencyNames.push(...normalized);
    for (const rule of PY_RULES) {
      const hit = rule.packages.find((p) => normalized.includes(p));
      if (hit) addTech({ id: rule.tech, label: rule.label, evidence: `requirements: ${hit}` });
    }
  }
  if (input.dependencies?.go) addTech({ id: 'go', label: 'Go', evidence: 'go.mod' });
  if (input.dependencies?.rust) addTech({ id: 'rust', label: 'Rust', evidence: 'Cargo.toml' });

  if (input.treePaths?.length) {
    // Only match markers at the repo root or one level deep — deps of examples/fixtures don't define the stack.
    const shallow = new Set(
      input.treePaths
        .filter((p) => p.split('/').length <= 2)
        .map((p) => p.toLowerCase())
    );
    for (const rule of FILE_RULES) {
      const hit = rule.markers.find(
        (m) => shallow.has(m) || [...shallow].some((p) => p.endsWith(`/${m}`))
      );
      if (hit) addTech({ id: rule.tech, label: rule.label, evidence: `file: ${hit}` });
    }
  }

  if (input.languages) {
    const langs = Object.keys(input.languages).map((l) => l.toLowerCase());
    for (const rule of LANGUAGE_RULES) {
      if (langs.includes(rule.language)) {
        addTech({ id: rule.tech, label: rule.label, evidence: 'GitHub language stats' });
      }
    }
  }

  // JS is implied (and superseded) when TS is present.
  if (techs.has('typescript')) techs.delete('javascript');

  return {
    repoFullName: input.repoFullName,
    techs: [...techs.values()],
    dependencyNames: [...new Set(dependencyNames)],
  };
}
